import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { formatPence } from '@hcd/domain'
import {
  BadgeCheck,
  CalendarClock,
  Eye,
  FileCheck2,
  FileSignature,
  Inbox,
  Plus,
  Send,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { keyDate } from '@/components/contracts/views'
import { libraryQuery } from '@/components/library/api'
import { StartersPanel } from '@/components/onboarding/dashboard-panel'
import { EnquiryStatusBadge } from '@/components/enquiries/views'
import { NewQuoteDialog } from '@/components/quotes/new-quote-dialog'
import { StatusBadge } from '@/components/quotes/status-badge'
import { PageHeader } from '@/components/shell/page'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { contractsQuery } from '@/lib/contracts'
import { displayName, enquiriesQuery, SOURCE_LABEL } from '@/lib/enquiries'
import { PER, quotesQuery, ukDate, type QuoteSummary } from '@/lib/quotes'
import { can } from '@/lib/roles'
import { firstName } from '@/lib/session'
import { useTitle } from '@/lib/use-title'
import { attentionQuery } from '@/lib/attention'

export const Route = createFileRoute('/_app/')({ component: HomePage })

function greeting(d = new Date()) {
  const h = d.getHours()
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}

const money = (q: QuoteSummary) =>
  q.periods.map((p) => `${formatPence(p.grossPence)} ${PER[p.key]}`).join(' + ')

function HomePage() {
  useTitle('Home')
  const { me } = Route.useRouteContext()
  const [creating, setCreating] = useState(false)
  const quotes = useQuery(quotesQuery())
  const library = useQuery(libraryQuery)
  const contracts = useQuery(contractsQuery())
  const enquiries = useQuery(enquiriesQuery())
  const attention = useQuery(attentionQuery)
  const today = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date())

  const all = quotes.data ?? []
  const withClients = all.filter((q) => q.status === 'sent' || q.status === 'viewed')
  const accepted = all.filter((q) => q.status === 'accepted').slice(0, 5)
  const fresh = (enquiries.data ?? []).filter((e) => e.status === 'new').slice(0, 5)
  const soon = new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10)
  const expiring = (library.data ?? [])
    .filter((d) => d.active && d.current?.expiresOn && d.current.expiresOn <= soon)
    .sort((a, b) => a.current!.expiresOn!.localeCompare(b.current!.expiresOn!))

  const reviews = (contracts.data ?? [])
    .filter((c) => c.reviewDue || (c.status === 'notice_given' && c.endDate))
    .sort((a, b) =>
      (a.status === 'notice_given' ? a.endDate! : a.reviewDate!).localeCompare(
        b.status === 'notice_given' ? b.endDate! : b.reviewDate!,
      ),
    )
    .slice(0, 5)

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${firstName(me)}`}
        description={today}
        actions={
          <Button className="h-10" onClick={() => setCreating(true)}>
            <Plus />
            New quote
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ListPanel
          title="New enquiries"
          icon={<Inbox />}
          loading={enquiries.isPending}
          empty="No new enquiries waiting."
          to="/enquiries"
          search={{ show: 'new' }}
        >
          {fresh.map((e) => (
            <li key={e.id}>
              <Link
                to="/enquiries/$id"
                params={{ id: e.id }}
                className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 hover:bg-secondary/60"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{displayName(e)}</span>
                  <span className="block truncate text-sm text-muted-foreground">
                    {e.reference} · {SOURCE_LABEL[e.source]} · {ukDate(e.createdAt, true)}
                  </span>
                </span>
                <EnquiryStatusBadge status={e.status} />
              </Link>
            </li>
          ))}
        </ListPanel>
        <ListPanel
          title="To check"
          icon={<BadgeCheck />}
          loading={attention.isPending}
          empty="Nothing is waiting on you."
          to="/approvals"
        >
          {[
            ...(attention.data?.agentDrafts ?? []).map((q) => ({ q, why: 'Drafted by Kira' })),
            ...(attention.data?.awaitingApproval ?? []).map((q) => ({
              q,
              why: 'Waiting for approval',
            })),
            ...(attention.data?.expiringSoon ?? []).map((q) => ({
              q,
              why: `Expires ${ukDate(q.validUntil)}`,
            })),
          ]
            .slice(0, 5)
            .map(({ q, why }) => (
              <li key={q.id}>
                <Link
                  to="/quotes/$id"
                  params={{ id: q.id }}
                  className="-mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-2.5 hover:bg-secondary/60"
                >
                  <span className="min-w-0 truncate font-medium">
                    {q.reference} · {q.customerName || q.title}
                  </span>
                  <span className="shrink-0 text-sm text-muted-foreground">{why}</span>
                </Link>
              </li>
            ))}
        </ListPanel>
        <ListPanel
          title="With clients"
          icon={<Send />}
          loading={quotes.isPending}
          empty="No proposals are out with clients."
          to="/quotes"
          search={{ show: 'client' }}
        >
          {withClients.slice(0, 5).map((q) => (
            <QuoteRow
              key={q.id}
              q={q}
              meta={
                q.lastViewedAt ? (
                  <span className="inline-flex items-center gap-1">
                    <Eye className="size-3.5" aria-hidden /> Viewed {ukDate(q.lastViewedAt, true)}
                  </span>
                ) : (
                  `Sent ${ukDate(q.sentAt)}, not opened yet`
                )
              }
            />
          ))}
        </ListPanel>
        <ListPanel
          title="Recently accepted"
          icon={<FileCheck2 />}
          loading={quotes.isPending}
          empty="No accepted quotes yet."
          to="/quotes"
          search={{ show: 'accepted' }}
        >
          {accepted.map((q) => (
            <QuoteRow key={q.id} q={q} meta={`Accepted ${ukDate(q.acceptedAt)} · ${money(q)}`} />
          ))}
        </ListPanel>
        <ListPanel
          title="Documents expiring in 30 days"
          icon={<CalendarClock />}
          loading={library.isPending}
          empty="No company documents are about to expire."
          to="/library"
        >
          {expiring.map((d) => {
            const expired = d.current!.expiresOn! < new Date().toISOString().slice(0, 10)
            return (
              <li key={d.id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="font-medium">{d.title}</span>
                <span
                  className={`text-sm font-medium ${expired ? 'text-destructive' : 'text-warning'}`}
                >
                  {expired
                    ? `Expired ${ukDate(d.current!.expiresOn)}`
                    : `Expires ${ukDate(d.current!.expiresOn)}`}
                </span>
              </li>
            )
          })}
        </ListPanel>
        <ListPanel
          title="Contract reviews due"
          icon={<FileSignature />}
          loading={contracts.isPending}
          empty="No contract reviews or notice periods need attention in the next 60 days."
          to="/contracts"
          search={{ show: 'review' }}
        >
          {reviews.map((c) => (
            <li key={c.id}>
              <Link
                to="/contracts/$id"
                params={{ id: c.id }}
                className="-mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-2.5 hover:bg-secondary/60"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">
                    {c.customerName}{' '}
                    <span className="font-normal text-muted-foreground">· {c.reference}</span>
                  </span>
                  <span className="block truncate text-sm text-muted-foreground">
                    {c.siteName || c.contractTypeName}
                  </span>
                </span>
                <span className="shrink-0 text-sm font-medium text-[#7a4d0e] dark:text-warning">
                  {keyDate(c).text}
                </span>
              </Link>
            </li>
          ))}
        </ListPanel>
        {can.managePeople(me) && <StartersPanel />}
      </div>
      <NewQuoteDialog open={creating} onOpenChange={setCreating} />
    </>
  )
}

function ListPanel({
  title,
  icon,
  loading,
  empty,
  children,
  to,
  search,
}: {
  title: string
  icon: ReactNode
  loading: boolean
  empty: string
  children: ReactNode[]
  to: '/quotes' | '/library' | '/contracts' | '/enquiries' | '/approvals'
  search?: { show: string }
}) {
  return (
    <section className="rounded-xl border bg-card">
      <header className="flex items-center justify-between gap-2 border-b px-5 py-3.5">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold">
          <span className="text-primary [&_svg]:size-4">{icon}</span>
          {title}
        </h2>
        {to === '/quotes' ? (
          <Link
            to="/quotes"
            search={search}
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            See all
          </Link>
        ) : to === '/approvals' ? (
          <Link
            to="/approvals"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            See all
          </Link>
        ) : to === '/contracts' ? (
          <Link
            to="/contracts"
            search={search as { show: 'review' }}
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            See all
          </Link>
        ) : to === '/enquiries' ? (
          <Link
            to="/enquiries"
            search={search}
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            See all
          </Link>
        ) : (
          <Link
            to="/library"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Open library
          </Link>
        )}
      </header>
      <div className="px-5 py-2">
        {loading ? (
          <Skeleton className="my-2 h-16" />
        ) : children.length === 0 ? (
          <p className="py-3 text-[15px] text-muted-foreground">{empty}</p>
        ) : (
          <ul className="divide-y">{children}</ul>
        )}
      </div>
    </section>
  )
}

function QuoteRow({ q, meta }: { q: QuoteSummary; meta: ReactNode }) {
  return (
    <li>
      <Link
        to="/quotes/$id"
        params={{ id: q.id }}
        className="-mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 hover:bg-secondary/60"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">
            {q.title} <span className="font-normal text-muted-foreground">· {q.reference}</span>
          </span>
          <span className="tabular block truncate text-sm text-muted-foreground">{meta}</span>
        </span>
        <StatusBadge status={q.status} />
      </Link>
    </li>
  )
}
